import { useEffect, useRef } from 'react'
import {
  debounce,
  TDebounced,
  TDebounceable,
  TDebounceOpts,
} from '../../async/debounce'

export type TUseDebounce<T, E, A extends unknown[]> = TDebounced<T, E, A>

export const useDebounce = <T, E, A extends unknown[]>(
  fn: TDebounceable<T, E, A>,
  opts?: { delay?: number; abortOnUnmount?: boolean },
): TUseDebounce<T, E, A> => {
  const fnRef = useRef(fn)
  // eslint-disable-next-line react-compiler/react-compiler
  fnRef.current = fn
  const abortOnUnmountRef = useRef(opts?.abortOnUnmount ?? true)

  const debounced = useRef<TDebounced<T, E, A>>(
    // eslint-disable-next-line react-compiler/react-compiler
    debounce((args: A, opts?: TDebounceOpts) => {
      return fnRef.current(args, opts)
    }, opts?.delay),
  )

  useEffect(() => {
    if (!abortOnUnmountRef.current) return
    return () => {
      debounced.current?.abort('Aborted on component unmount.')
    }
  }, [])

  // eslint-disable-next-line react-compiler/react-compiler
  return debounced.current
}
